// @ts-nocheck

import * as R from 'ramda';
import { CUSTOM_DATASOURCES, CUSTOM_UNINSTALL } from '../../../../../shared/constants/dataFieldKeys/dataSources';
import { CUSTOM_EVENT_PREFIX } from '../../../../../shared/constants/dataFieldKeys/reportColumnKeys';
import { RATE_FOR } from '../../../../../shared/constants/dynamicMetrics';
import { getMetaDataFromColumnKey } from '../../../../../shared/parsers/dynamicCalculations';
import { isBlockedCustomEvent, isCustomEvent, isDynamicCalculation } from '../../../../../shared/utils/queryBuilder';
import type { Row } from '../../../main/types/tableTypes';
import type { IColumn } from '../../types/IColumn';
import { ALL_EVENT_NAMES_WITH_LOCAL_CURRENCY, DRUID_TO_HUMAN_READABLE } from '../eo_mapping';
import { EO_CUSTOM_EVENT, LOCAL_CURRENCY_CALCULATED_DATASOURCES } from '../eo_mapping/datasources';
import { LOCAL_CURRENCY_EVENTS_HUMAN_READABLE } from '../eo_mapping/event_names';
import { buildColumn } from './buildColumn';
import { getListOfDimensionsAndValues } from './druid/nativeQueries';
import {
  EVENT_TYPE_CURRENCY_FORMULA,
  EVENT_TYPE_DATA_SOURCES,
  EVENT_TYPE_EO_CUSTOM_EVENT,
  EVENT_TYPE_EO_CUSTOM_EVENT_BLOCKED,
  EVENT_TYPE_FORMULA,
  EVENT_TYPE_STANDARD_EVENT,
  TIME_SERIES_CALCULATION_TOPICS,
  UNDISCLOSED_EVENT_VALUE,
} from './eo_constants';

import {
  BLOCKED_CUSTOM_EVENT_PREFIX,
  REPORT_COLUMN_KEYS,
} from '../../../../../shared/constants/dataFieldKeys/reportColumnKeys';

export function getDynamicCustomEventLabel(calculationId: string, eventName: string): string {
  const eventNameWithoutPrefix = eventName.slice(CUSTOM_EVENT_PREFIX.length);

  if (calculationId === 'rate_for') {
    return `${eventNameWithoutPrefix} rate`;
  }

  return `${calculationId.replace(/_/g, ' ')} ${eventNameWithoutPrefix}`;
}

export function getDynamicEventLabel(key: string): string {
  const { eventName, calculationId } = getMetaDataFromColumnKey(key);

  if (eventName?.startsWith(CUSTOM_EVENT_PREFIX)) {
    return getDynamicCustomEventLabel(calculationId, eventName);
  }

  const parsedEventName = DRUID_TO_HUMAN_READABLE[eventName] || eventName;

  if (calculationId === RATE_FOR) {
    return `${parsedEventName} rate`;
  }

  return `${calculationId.replace(/_/g, ' ')} ${parsedEventName}`;
}

export const getColumnDisplay = (key: string): string => {
  if (DRUID_TO_HUMAN_READABLE[key]) {
    return DRUID_TO_HUMAN_READABLE[key];
  }

  if (isDynamicCalculation(key)) {
    return getDynamicEventLabel(key);
  }

  if (isCustomEvent(key)) {
    if (key === CUSTOM_UNINSTALL) {
      return CUSTOM_DATASOURCES[CUSTOM_UNINSTALL];
    }

    return key.slice(CUSTOM_EVENT_PREFIX.length);
  }

  if (isBlockedCustomEvent(key)) {
    return 'blocked ' + key.slice(BLOCKED_CUSTOM_EVENT_PREFIX.length);
  }

  return key;
};

/**
 * Recursively resolve all data sources needed for a formula
 * Treat revenue as a special data source and map it to `eo_commerce_event`
 * @param  {Array<string>} sources
 * @return {Array<string>}
 */
function getDataSourcesForFormula(sources) {
  if (!sources) {
    return [];
  }

  return sources.flatMap((source) => {
    if (typeof source === 'string') {
      return ALL_EVENT_NAMES_WITH_LOCAL_CURRENCY[source] ? ALL_EVENT_NAMES_WITH_LOCAL_CURRENCY[source] : source;
    }

    return getDataSourcesForFormula(source.fields);
  });
}

/**
 * Fetch the data source/s corresponding to the given type of event
 * @param  {String} type
 * @param  {Array<String>} sources
 * @param  {string} key
 * @return {Array<String>}  return a list of data sources corresponding to
 * the given type
 */
export function getUnderlyingDataSources(type: string, sources?: any, key: string): Array<any> | null | undefined {
  switch (type) {
    case EVENT_TYPE_DATA_SOURCES:
      return [key];

    case EVENT_TYPE_STANDARD_EVENT:
      return sources && [sources[key]];

    case EVENT_TYPE_EO_CUSTOM_EVENT:
    case EVENT_TYPE_EO_CUSTOM_EVENT_BLOCKED:
      return [type];

    case EVENT_TYPE_FORMULA:
    case EVENT_TYPE_CURRENCY_FORMULA:
      return getDataSourcesForFormula(sources);

    default:
      return null;
  }
}

/**
 * Used to add additional columns or metadata to a column based on the contents of the column
 * NOTE: this context contains info about which columns to show calculate formulas for
 * we can add more info to this context when needed
 this = {
    formulas: ['eo_install', 'eo_reinstall']
 }
 *
 */
export function getColumnMetadata(
  type: string,
  sources?: any,
  key: string,
  extensions = {},
  localCurrency?: string
): Record<string, any> {
  const addAllFormulas = this === undefined || !('formulas' in this);
  const formulas = this?.formulas;
  return buildColumn({
    type,
    key,
    sources,
    extensions,
    addAllFormulas,
    formulas,
    localCurrency,
  });
}
export const getServerSideTopicNamesForTrends = (key: string): string => {
  const display = getColumnDisplay(key);

  const MAPPED_TOPICS_CLIENT_TO_SERVER = { ...REPORT_COLUMN_KEYS, ...TIME_SERIES_CALCULATION_TOPICS };

  if (MAPPED_TOPICS_CLIENT_TO_SERVER[key]) {
    return MAPPED_TOPICS_CLIENT_TO_SERVER[key];
  } else if (isCustomEvent(key)) {
    return `custom_event__${display}`;
  }

  return key;
};

/*
 * Helper function applying an operator over a list of results
 */
export function calculate(fn: string, results: number[]): number {
  switch (fn) {
    case '+':
      return results.reduce((acc, curr) => acc + curr, 0);

    case '-':
      return results.reduce((acc, curr, i) => {
        if (i === 0) {
          return curr;
        }

        return !curr ? curr : acc - curr;
      }, 0);

    case '*':
      return results.reduce((acc, curr, i) => {
        if (i === 0) {
          return curr;
        }

        return !curr ? curr : acc * curr;
      }, 0);

    case '/':
    default:
      return results.reduce((acc, curr, i) => {
        if (i === 0) {
          return curr;
        }

        const isResultUndisclosed = curr === UNDISCLOSED_EVENT_VALUE || acc === UNDISCLOSED_EVENT_VALUE;

        if (isResultUndisclosed) {
          return UNDISCLOSED_EVENT_VALUE;
        }

        return !curr ? curr : acc / curr;
      }, 0);
  }
}

/**
 * Generates a formula used to concatenate fields as a growing string
 */
export function generateConcatFormula(
  fields: Array<string | number | Record<string, any>>
): (row: Record<string, any>) => any {
  return function (row) {
    return fields.reduce((acc, curr) => {
      switch (typeof curr) {
        case 'string':
          return acc + row[curr];

        default:
          return acc;
      }
    }, '');
  };
}

/**
 * Generates a formula used to calculate a derived column value such as "click to install percentag"
 */
export function generateFormula(
  fn: string,
  fields: Array<string | number | Record<string, any>>
): (row: Record<string, any>) => any {
  return function (row) {
    function recurse(fn1, fields1) {
      const results = fields1.map((f) => {
        switch (typeof f) {
          case 'object':
            return recurse(f.fn, f.fields);

          case 'number':
            return f;

          case 'string':
            return row[f];

          default:
            return 0;
        }
      }, []);
      return calculate(fn1, results);
    }

    return recurse(fn, fields);
  };
}

function _getFormulaColumnMetadata(parts: {
  key: string;
  type?: string;
  fn: string;
  fields: Array<string | number | Record<string, any>>;
}): Record<string, any> {
  const { key, type, fn, fields } = parts;
  const metadata = getColumnMetadata(type || 'formula', fields, key);

  if (fn === 'concat') {
    return R.merge(metadata, {
      getValue: generateConcatFormula(fields),
    });
  }

  return R.merge(metadata, {
    getValue: generateFormula(fn, fields),
  });
}

export function injectColumnFormulaFromMetadata(columns: IColumn[]): IColumn[] {
  return R.pipe(
    R.map((column) => {
      if (!column.formulas) {
        return column;
      }

      return [column, ...column.formulas.map(_getFormulaColumnMetadata)];
    }),
    R.flatten
  )(columns);
}
export function injectColumnsLocalCurrency(columns: IColumn[], localCurrency: string): IColumn[] {
  return columns.map((column) => {
    if (LOCAL_CURRENCY_CALCULATED_DATASOURCES.includes(column.key)) {
      column.localCurrency = localCurrency;
    }

    return column;
  });
}
export function injectTimeseriesLocalCurrency(column: IColumn, localCurrency: string): IColumn {
  if (LOCAL_CURRENCY_EVENTS_HUMAN_READABLE.includes(column.key)) {
    column.localCurrency = localCurrency;
  }

  return column;
}
export const getRowValueHash = (row: Record<string, any>, dimensions: Array<string>) => {
  return dimensions.reduce((hash, dimension) => {
    const value = row[dimension];

    if (!hash) {
      return `${value}`;
    }

    return `${hash}-${value}`;
  }, '');
};
export const generateRowHashMap = (rows: Record<string, any>[], dimensions: Array<string>) => {
  return rows.reduce((hashMap, row) => {
    const key = getRowValueHash(row, dimensions);
    return R.assoc(key, row, hashMap);
  }, {});
};
export const isAppPlatform = R.contains(R.__, ['IOS_APP', 'ANDROID_APP', 'DESKTOP_APP', 'TV_APP']);
export const WEB_COPY_KEYS = ['eo_click', 'eo_impression'];
export function copyWebClicksToAppClicks(dimensions: string[], data: Record<string, any>[]) {
  const PLATFORMS = {
    IOS_APP: 'IOS_WEB',
    ANDROID_APP: 'ANDROID_WEB',
    DESKTOP_APP: 'DESKTOP_WEB',
    TV_APP: 'TV_WEB',
  };
  const rowHashMap = generateRowHashMap(data, dimensions);
  return data.map((row) => {
    const refPlatform = PLATFORMS[row.user_data_platform];

    // if there is no corresponding lookup platform i.e. DESKTOP_WEB, return early
    if (!refPlatform) {
      return row;
    }

    // find the corresponding reference row w/ matching dimensions
    const rowKey = getRowValueHash(R.assoc('user_data_platform', refPlatform, row), dimensions);
    const refRow = rowHashMap[rowKey];

    // early exit if no corresponding reference row
    if (!refRow) {
      return row;
    }

    // mutate the copy values for app platform from web platform
    return R.merge(row, R.pick(WEB_COPY_KEYS, refRow));
  });
}
export function copyWebClicksToAppTotalRowFilter(row: Row) {
  if (!isAppPlatform(row.user_data_platform)) {
    return row;
  }

  return R.omit(WEB_COPY_KEYS, row);
}

/* DANA-780 HELPERS END */
// EventType as defined in app-service:
// https://github.com/BranchMetrics/app-service/blob/master/app-application/resources/app.proto#L40
export function formatEoDatasource(eoDatasource: string): string {
  if (!eoDatasource) {
    return '';
  }

  // remove eo_
  const eventType = eoDatasource.slice(3);
  return eventType.toUpperCase();
}
export function getEventDatasource(event: string): string {
  const eoDatasource = getEoDatasource(event);
  return formatEoDatasource(eoDatasource);
}
export function getEoDatasource(event: string): string {
  if (isCustomEvent(event)) {
    return EO_CUSTOM_EVENT;
  }

  return ALL_EVENT_NAMES_WITH_LOCAL_CURRENCY[event];
}
export function mapToEventName(name: string): string {
  if (name.startsWith(EO_CUSTOM_EVENT)) {
    return name.replace(`${EO_CUSTOM_EVENT}-`, '');
  }

  return name;
}

/**
 * Convert from human readable value to appropriate druid query value
 */
export function convertHumanReadableToDruid(humanReadable: string) {
  return Object.keys(DRUID_TO_HUMAN_READABLE).find((key) => {
    return DRUID_TO_HUMAN_READABLE[key] === humanReadable;
  });
}
const LAST_ATTRIBUTED_TOUCH_TYPES = ['eo_click', 'eo_impression'];
// Warning on last_attributed_touch_type temporarily removed until EG-1116.
// https://branch.atlassian.net/browse/EG-2539
const DIMENSION_BLACK_LIST = [
  // 'last_attributed_touch_type',
  'first_event_for_user',
];
export function generateLastTouchWarningText(filter: Record<string, any>, columnKeys: Record<string, any>) {
  const touchTypes = columnKeys.filter((key) => R.contains(key, LAST_ATTRIBUTED_TOUCH_TYPES));
  const dimensionsToValues = getListOfDimensionsAndValues(filter);
  const blacklistDimensions = DIMENSION_BLACK_LIST.filter(
    (dimension) => R.path([dimension, 'length'], dimensionsToValues) > 0
  );

  if (touchTypes.length < 1 || blacklistDimensions.length < 1) {
    return;
  }

  const humanReadableDataSources = touchTypes.map((type) => DRUID_TO_HUMAN_READABLE[type] || type);
  const humanReadableDimensions = blacklistDimensions.map((dimension) => DRUID_TO_HUMAN_READABLE[dimension] || dimension);
  return `This filter does not filter on ${humanReadableDimensions.join(' or ')} for ${humanReadableDataSources.join(
    ' or '
  )}, which do not have ${blacklistDimensions.length > 1 ? '' : 'a'} ${humanReadableDimensions.join(' or ')}.`;
}
type column = {
  popover: string;
  type: string;
  key: string;
  display: string;
};
export function limitColumnsByKeys(columns: column[], keysToExclude: string[]): column[] {
  if (!Array.isArray(columns) || !columns.length) {
    return [];
  }

  if (!keysToExclude || !Array.isArray(keysToExclude) || !keysToExclude.length) {
    return columns;
  }

  return columns.filter((column) => keysToExclude.indexOf(column.key) < 0);
}
